import { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    useTheme,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import Axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import { useSnackbar } from 'notistack';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import ParentPortalButton from '../../components/ParentPortalButton';

const ParentPortalLinkPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();

    const [consultationNumber, setConsultationNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searched, setSearched] = useState(false);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const handleSearch = async () => {
        if (!consultationNumber.trim()) {
            enqueueSnackbar('상담번호를 입력해주세요.', { variant: 'warning' });
            return;
        }

        if (isNaN(consultationNumber.trim())) {
            enqueueSnackbar('상담번호는 숫자만 입력 가능합니다.', { variant: 'warning' });
            return;
        }

        setLoading(true);
        setSearched(true);

        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/students/consultation-progress`,
                {
                    params: { consultation_number: consultationNumber.trim() },
                    headers: { Authorization: getAuthHeader() },
                }
            );

            if (response.data && response.data.length > 0) {
                setSearchResults(response.data);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error searching:', error);
                enqueueSnackbar('검색 중 오류가 발생했습니다.', { variant: 'error' });
                setSearchResults([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const getStudentDisplayName = (student) => {
        let name = '';
        if (student.first_name) name += student.first_name;
        if (student.korean_name) name += ` ${student.korean_name}`;
        if (student.last_name) name += ` ${student.last_name}`;
        return name.trim() || 'Name Unknown';
    };

    return (
        <Box m="20px">
            <Header
                title="학부모 포탈 바로가기"
                subtitle="상담번호로 검색하여 학부모 포탈에 바로 접속할 수 있는 링크를 생성합니다."
            />

            {/* Search Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" mb={2} fontWeight="bold">
                    상담번호 검색
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                    <TextField
                        label="상담번호"
                        variant="outlined"
                        value={consultationNumber}
                        onChange={(e) => setConsultationNumber(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="예: 1234"
                        sx={{ minWidth: 200 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{ height: 56, px: 3 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : '검색'}
                    </Button>
                </Box>
            </Paper>

            {/* Results Section */}
            {searched && !loading && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" mb={2}>
                        검색 결과
                    </Typography>

                    {searchResults.length === 0 ? (
                        <Typography color="text.secondary">
                            검색 결과가 없습니다. 상담번호를 다시 확인해주세요.
                        </Typography>
                    ) : (
                        <>
                            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                                {searchResults.map((student) => (
                                    <Box key={student.uuid} display="flex" alignItems="center" gap={1}>
                                        <PersonIcon sx={{ color: colors.greenAccent[400] }} />
                                        <Typography>
                                            {getStudentDisplayName(student)}
                                            {student.grade && ` (${student.grade}학년)`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            <ParentPortalButton
                                parentEmail={searchResults[0].parent_email || searchResults[0].parent_email_2}
                                consultationNumber={searchResults[0].consultation_number}
                                studentUuid={searchResults[0].uuid}
                                sx={{
                                    backgroundColor: colors.greenAccent[600],
                                    color: '#fff',
                                    px: 3,
                                    py: 1,
                                    '&:hover': {
                                        backgroundColor: colors.greenAccent[500],
                                    },
                                }}
                            />
                        </>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ParentPortalLinkPage;
